import * as React from 'react';
import { cn } from '../lib/utils';

const TABLE_HEAD_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('bg-muted [&_tr]:border-b [&_tr]:border-border', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={cn(className)} {...props} />);
TableBody.displayName = 'TableBody';

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  listRow?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, listRow = false, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-border transition-colors',
        listRow && 'hover:bg-muted/10',
        listRow && 'border-border/30 last:border-b-0',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: (typeof TABLE_HEAD_ALIGN)[keyof typeof TABLE_HEAD_ALIGN];
  caps?: boolean;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align = TABLE_HEAD_ALIGN.LEFT, caps = true, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        // Defaults to wrapping (not `whitespace-nowrap`) so every table — DataTable
        // and hand-built alike — lets a long header label break onto a second line
        // instead of clipping mid-word once its column narrows below the label's
        // natural width. `className` is applied last, so any caller/column-level
        // override (e.g. an explicit `whitespace-nowrap`) still wins via `cn()`.
        'bg-muted px-4 py-3 align-middle text-[12px] font-medium text-muted-foreground whitespace-normal break-words',
        align === TABLE_HEAD_ALIGN.LEFT && 'text-left',
        align === TABLE_HEAD_ALIGN.CENTER && 'text-center',
        align === TABLE_HEAD_ALIGN.RIGHT && 'text-right',
        caps ? 'uppercase tracking-wider' : 'normal-case tracking-normal',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn('align-middle', className)} {...props} />
));
TableCell.displayName = 'TableCell';

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TABLE_HEAD_ALIGN };
